import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { loadPly } from '../../lib/threejs/ply';
import { OBJLoader } from '../../lib/threejs/OBJLoader';
import { loadMarginPoints } from '../../lib/threejs/margin';
import { TrackballControls } from '../../lib/threejs/TrackballControls';
import { TransformControls } from '../../lib/threejs/TransformControls';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import SliderBox from './CaseDetail3DSlider';
import { setDownloadFileList } from '../../lib/api/case';
import SVG from '../common/svg';
import { ThemeContext } from 'styled-components';
import { fullScreen } from '../../modules/case';
import CaseDetail3DPreview from './CaseDetail3DPreview';
import { TabletOrDesktop } from '../../lib/responsive';
import FindingsOverlay from './CaseDetail3DFindings';
import {
  clearFindingMarkers,
  createFindingMarkers,
  getFindingKey,
  updateFindingMarkers,
} from '../../lib/threejs/findingMarkers';
import {
  applyMeshTransform,
  getMeshDisplayRotationX,
  getMeshDisplayRotationY,
  getMeshDisplayRotationZ,
  getOwnerMeshType,
} from '../../lib/threejs/meshTransforms';

const AVA_UPPER_MESH_TYPES = new Set([0, 1, 2]);
const AVA_LOWER_MESH_TYPES = new Set([3, 4, 5]);
const AVA_DEVICE_MODELS = new Set(['3disc_ava', '3disc_avaplus', '3disc_ovo3']);

const AVA_SOFTWARE_VERTEX_SHADER = `
  varying vec3 vMeshColor;
  varying vec3 vPositionView;
  varying vec3 vNormalView;

  void main() {
    vec4 positionView = modelViewMatrix * vec4(position, 1.0);
    vMeshColor = color;
    vPositionView = positionView.xyz;
    vNormalView = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * positionView;
  }
`;

const AVA_SOFTWARE_FRAGMENT_SHADER = `
  uniform float uOpacity;
  uniform float uSpecularEnabled;
  varying vec3 vMeshColor;
  varying vec3 vPositionView;
  varying vec3 vNormalView;

  const float PI = 3.14159265359;

  vec3 adjustHue(vec3 rgb, float adjustment) {
    adjustment *= PI;
    const mat3 toYiq = mat3(
      0.299, 0.587, 0.114,
      0.595716, -0.274453, -0.321263,
      0.211456, -0.522591, 0.311135
    );
    const mat3 toRgb = mat3(
      1.0, 0.9563, 0.6210,
      1.0, -0.2721, -0.6474,
      1.0, -1.107, 1.7046
    );
    vec3 yiq = toYiq * rgb;
    float hue = atan(yiq.z, yiq.y) + adjustment;
    float chroma = sqrt(yiq.z * yiq.z + yiq.y * yiq.y);
    return toRgb * vec3(yiq.x, chroma * cos(hue), chroma * sin(hue));
  }

  vec3 adjustSaturation(vec3 rgb, float adjustment) {
    const vec3 weights = vec3(0.2125, 0.7154, 0.0721);
    vec3 intensity = vec3(dot(rgb, weights));
    return mix(intensity, rgb, adjustment);
  }

  void main() {
    vec3 normalView = normalize(vNormalView);
    if (!gl_FrontFacing) normalView = -normalView;

    vec3 lightDirection = normalize(vec3(-0.29, -0.77, -0.56));
    vec3 eyeDirection = normalize(-vPositionView);
    vec3 reflected = normalize(
      -reflect(lightDirection, normalView * 2.0 - 1.0)
    );
    float specularCoefficient = pow(
      max(dot(reflected, eyeDirection), 0.0),
      90.0
    );

    vec3 rgb = vMeshColor;
    rgb += clamp(
      vec3(0.8 * uSpecularEnabled) * specularCoefficient,
      0.0,
      1.0
    );
    rgb = adjustHue(rgb, 0.04);
    rgb = adjustSaturation(rgb, 1.08);
    rgb = rgb * 1.2 - vec3(0.12);
    rgb *= vec3(1.04, 1.0, 1.12);

    gl_FragColor = vec4(rgb, uOpacity);
  }
`;

const createAvaSoftwareMaterial = () =>
  new THREE.ShaderMaterial({
    uniforms: {
      uOpacity: { value: 1 },
      uSpecularEnabled: { value: 1 },
    },
    vertexShader: AVA_SOFTWARE_VERTEX_SHADER,
    fragmentShader: AVA_SOFTWARE_FRAGMENT_SHADER,
    vertexColors: true,
    transparent: true,
    opacity: 1,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: true,
    toneMapped: false,
  });

const createMeshMaterial = (useAvaRendering, vertexColors = false) => {
  const commonOptions = {
    color: 0xffffff,
    vertexColors,
    transparent: true,
    opacity: 1,
    side: THREE.DoubleSide,
  };

  if (useAvaRendering && vertexColors) {
    return createAvaSoftwareMaterial();
  }

  if (useAvaRendering) {
    return new THREE.MeshStandardMaterial({
      ...commonOptions,
      metalness: 0,
      roughness: 0.68,
    });
  }

  return new THREE.MeshPhongMaterial({
    ...commonOptions,
    specular: 0x111111,
    shininess: 60,
  });
};

const getRotatedBounds = (boundingBox, center, rotation) => {
  const bounds = new THREE.Box3().makeEmpty();

  for (const x of [boundingBox.min.x, boundingBox.max.x]) {
    for (const y of [boundingBox.min.y, boundingBox.max.y]) {
      for (const z of [boundingBox.min.z, boundingBox.max.z]) {
        bounds.expandByPoint(
          new THREE.Vector3(x, y, z).sub(center).applyEuler(rotation),
        );
      }
    }
  }

  return bounds;
};

const getCombinedBounds = (entries) =>
  entries.reduce(
    (bounds, entry) => bounds.union(entry.bounds),
    new THREE.Box3().makeEmpty(),
  );

const getCenteredJawOffset = (bounds, targetY = 0) => {
  const center = bounds.getCenter(new THREE.Vector3());
  return new THREE.Vector3(-center.x, targetY - center.y, 0);
};

const ResizeSensor = (element, callback) => {
  let zIndex = parseInt(getComputedStyle(element));
  if (isNaN(zIndex)) {
    zIndex = 0;
  }
  zIndex--;

  let expand = document.createElement('div');
  expand.style.position = 'absolute';
  expand.style.left = '0px';
  expand.style.top = '0px';
  expand.style.right = '0px';
  expand.style.bottom = '0px';
  expand.style.overflow = 'hidden';
  expand.style.zIndex = zIndex;
  expand.style.visibility = 'hidden';

  let expandChild = document.createElement('div');
  expandChild.style.position = 'absolute';
  expandChild.style.left = '0px';
  expandChild.style.top = '0px';
  expandChild.style.width = '10000000px';
  expandChild.style.height = '10000000px';
  expand.appendChild(expandChild);

  let shrink = document.createElement('div');
  shrink.style.position = 'absolute';
  shrink.style.left = '0px';
  shrink.style.top = '0px';
  shrink.style.right = '0px';
  shrink.style.bottom = '0px';
  shrink.style.overflow = 'hidden';
  shrink.style.zIndex = zIndex;
  shrink.style.visibility = 'hidden';

  let shrinkChild = document.createElement('div');
  shrinkChild.style.position = 'absolute';
  shrinkChild.style.left = '0px';
  shrinkChild.style.top = '0px';
  shrinkChild.style.width = '200%';
  shrinkChild.style.height = '200%';
  shrink.appendChild(shrinkChild);

  element.appendChild(expand);
  element.appendChild(shrink);

  function setScroll() {
    expand.scrollLeft = 10000000;
    expand.scrollTop = 10000000;

    shrink.scrollLeft = 10000000;
    shrink.scrollTop = 10000000;
  }
  setScroll();

  let size = element.getBoundingClientRect();

  let currentWidth = size.width;
  let currentHeight = size.height;

  let onScroll = function () {
    let size = element.getBoundingClientRect();

    let newWidth = size.width;
    let newHeight = size.height;

    if (newWidth !== currentWidth || newHeight !== currentHeight) {
      currentWidth = newWidth;
      currentHeight = newHeight;

      callback();
    }

    setScroll();
  };

  expand.addEventListener('scroll', onScroll);
  shrink.addEventListener('scroll', onScroll);

  return () => {
    expand.removeEventListener('scroll', onScroll);
    shrink.removeEventListener('scroll', onScroll);
    expand.remove();
    shrink.remove();
  };
};

const EMPTY_FINDINGS = Object.freeze([]);
const EMPTY_3D_FILES = Object.freeze([]);
const EMPTY_MESH_TRANSFORMS = Object.freeze({});

const BasicScene = ({
  files3D,
  fileBuffer,
  fileName,
  fileCount,
  fileType,
  findings = EMPTY_FINDINGS,
  meshTransforms = EMPTY_MESH_TRANSFORMS,
}) => {
  const { fullScreenState, settings, deviceModel } = useSelector(
    ({ caseDetail, user }) => ({
      fullScreenState: caseDetail.fullScreen,
      settings: user.settings,
      deviceModel: caseDetail.content?.device_model,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();
  const ref = useRef(null);
  const themeContext = useContext(ThemeContext);
  const resolvedFiles3D = useMemo(() => {
    if (files3D?.length) return files3D;
    if (fileBuffer && fileName) {
      return [{ id: fileName, buffer: fileBuffer, filename: fileName }];
    }

    return EMPTY_3D_FILES;
  }, [fileBuffer, fileName, files3D]);
  const useAvaDisplayOrientation = findings.some(
    (finding) => finding.positionSpace === 'view',
  );
  const useAvaRendering = AVA_DEVICE_MODELS.has(deviceModel);

  const screen = document.getElementById('fullscreen');

  const frustumSize = 50;
  const aspect = screen.clientWidth / 519;
  const [renderer] = useState(() => {
    const webGLRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    webGLRenderer.setPixelRatio(window.devicePixelRatio);
    webGLRenderer.setSize(screen.clientWidth, 519);
    webGLRenderer.outputEncoding = THREE.LinearEncoding;
    webGLRenderer.toneMapping = THREE.NoToneMapping;
    return webGLRenderer;
  });

  const [scene] = useState(new THREE.Scene());
  const [pivot] = useState(new THREE.Group());
  const [camera] = useState(() => {
    if (settings != null && settings._3d_projection !== 'perspective') {
      return new THREE.OrthographicCamera(
        (frustumSize * aspect) / -2, //left
        (frustumSize * aspect) / 2, //right
        frustumSize / 2, //top
        frustumSize / -2, //bottom
        0.5,
        2000,
      );
    }
    return new THREE.PerspectiveCamera(15, aspect, 0.5, 2000);
  });

  const [light] = useState(new THREE.DirectionalLight(0xffffff, 0.45));
  const [ambi_light] = useState(new THREE.AmbientLight(0xeda942, 0.6));
  const [fillLight] = useState(new THREE.DirectionalLight(0xdde8ff, 0));
  const [rimLight] = useState(new THREE.DirectionalLight(0xffead8, 0));
  const [arrObj, setArrObj] = useState([]);
  const [marginObj, setMarginObj] = useState([]);
  const [transControl] = useState(new TransformControls(camera, renderer.domElement));
  const [loading_order, setLoading_order] = useState([]);
  const [loading_order_margin, setLoading_order_margin] = useState([]);
  const [temp_colors] = useState(() => Array.from({ length: 6 }, () => []));
  const isTabletOrDesktop = TabletOrDesktop();
  const [geoLen] = useState([]);
  const [colors] = useState([]);
  const cameraZ = isTabletOrDesktop ? 400 : 600;
  const [trackball, setTrackball] = useState(null);
  const [findingGroup] = useState(new THREE.Group());
  const [meshCenter, setMeshCenter] = useState(null);
  const [findingDisplayOffsets, setFindingDisplayOffsets] = useState({});
  const [selectedFinding, setSelectedFinding] = useState(null);
  const findingHitTargets = useRef(new Map());
  const [isSolidClicked, setIsSolidClicked] = useState(false);
  const [isSpecularLightEnabled, setIsSpecularLightEnabled] = useState(true);
  const loadedFileIds = useRef(new Set());

  useEffect(() => {
    if (useAvaRendering) {
      renderer.outputEncoding = THREE.LinearEncoding;
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.toneMappingExposure = 1;
      light.color.setHex(0xffffff);
      light.intensity = 0.9;
      ambi_light.color.setHex(0xffffff);
      ambi_light.intensity = 0.18;
      fillLight.intensity = 0.35;
      rimLight.intensity = 0.25;
    } else {
      renderer.outputEncoding = THREE.LinearEncoding;
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.toneMappingExposure = 1;
      light.color.setHex(0xffffff);
      light.intensity = 0.45;
      ambi_light.color.setHex(0xeda942);
      ambi_light.intensity = 0.6;
      fillLight.intensity = 0;
      rimLight.intensity = 0;
    }
  }, [ambi_light, fillLight, light, renderer, rimLight, useAvaRendering]);

  const MINI_SCENE_WIDTH = 250; // pixels
  const MINI_SCENE_HEIGHT = 250; // pixels

  const onMaximize = () => {
    document.querySelector('#fullscreen').classList.add('FullScreen');
    renderer.domElement.classList.remove('cornerRound');
    dispatch(fullScreen(true));
  };
  const onClose = () => {
    document.querySelector('#fullscreen').classList.remove('FullScreen');
    renderer.domElement.classList.add('cornerRound');
    dispatch(fullScreen(false));
  };

  useEffect(() => {
    camera.aspect = screen.clientWidth / screen.clientHeight;
    renderer.setSize(screen.clientWidth, screen.clientHeight);
    camera.updateProjectionMatrix();
  }, [fullScreenState]);

  useEffect(() => {
    if (resolvedFiles3D.length > 0) {
      console.debug('useEffect, First Loading');
      const refcopy = ref;

      scene.add(pivot);
      pivot.add(findingGroup);
      scene.background = new THREE.Color(0xf0f1f2);
      scene.fog = new THREE.Fog(0xffffff, 100, 2000);

      camera.position.set(0, 0, cameraZ);
      if (refcopy.current !== null) refcopy.current.appendChild(renderer.domElement);

      renderer.domElement.classList.add('cornerRound');

      //Light
      light.position.set(70, 100, 200);
      fillLight.position.set(-160, 40, 120);
      rimLight.position.set(100, -80, -120);
      scene.add(light);
      scene.add(ambi_light);
      scene.add(fillLight);
      scene.add(rimLight);

      //Zoom
      const orbit = new OrbitControls(camera, renderer.domElement);
      orbit.enablePan = false;
      orbit.enableRotate = false;
      orbit.minDistance = 70;
      orbit.maxDistance = 600;
      orbit.minZoom = 0.25;
      orbit.maxZoom = 3;

      //Pan
      const trackballControls = new TrackballControls(camera, renderer.domElement);
      trackballControls.noPan = false;
      trackballControls.noRotate = false;
      trackballControls.noZoom = true;
      trackballControls.panSpeed = 0.6;
      trackballControls.staticMoving = true;
      trackballControls.dynamicDampingFactor = 0.3;

      setTrackball(trackballControls);

      //Rotate
      transControl.setMode('rotate');
      // transControl.attach(pivot); // DEBUG
      // transControl.visible = true; // DEBUG
      // transControl.enabled = true; // DEBUG
      // transControl.size = 1.5; //DEBUG
      scene.add(transControl);

      let animationFrameId;
      const animate = () => {
        animationFrameId = requestAnimationFrame(animate);
        trackballControls.update();
        updateFindingMarkers(findingGroup, camera, renderer, findingHitTargets.current);
        renderer.clear();
        renderer.render(scene, camera);

        // renderer.clearDepth(); // important!
        // renderer.setViewport(20, 20, screen.clientWidth, screen.clientHeight);
      };

      //Resize
      const stopResizeSensor = ResizeSensor(screen, function () {
        camera.aspect = screen.clientWidth / screen.clientHeight;
        renderer.setSize(screen.clientWidth, screen.clientHeight);
        camera.updateProjectionMatrix();
      });

      animate();

      return () => {
        console.debug('Clean up 3D Scene!!!');
        cancelAnimationFrame(animationFrameId);
        stopResizeSensor();
        orbit.dispose();
        trackballControls.dispose();
        transControl.dispose();
        renderer.dispose();
        if (refcopy.current != null) {
          refcopy.current.removeChild(renderer.domElement);
        }
        loadedFileIds.current.clear();
        setDownloadFileList({});
      };
    }
  }, []);

  //Load mesh for file extension
  useEffect(() => {
    resolvedFiles3D.forEach(({ id, buffer: fileBuffer, filename: fileName }) => {
      const fileId = id ?? fileName;
      if (loadedFileIds.current.has(fileId)) return;

      loadedFileIds.current.add(fileId);

      if (fileName.search('margin') < 0) {
        setLoading_order((state) => [...state, fileName]);
      } else if (fileName.search('margin') > 0) {
        setLoading_order_margin((state) => [...state, fileName]);
      }
      console.debug('Loading 3D File!!!!!!!!!!!!!!!!!!');

      if (fileName.search('.stl') > 0) {
        const material = createMeshMaterial(useAvaRendering);
        material.polygonOffset = true;
        material.polygonOffsetFactor = 5;

        // light.color.setHex(0xffffff);
        // light.intensity = 1;
        // light.position.set(0, 0, 30);
        // scene.remove(ambi_light);
        // material.color.setHex( 0xf2f2f2 );

        var sloader = new STLLoader();
        const geometry = sloader.parse(fileBuffer);
        applyMeshTransform(geometry, meshTransforms, fileName);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData.fileName = fileName;
        mesh.userData.displayObject = mesh;

        //Rotate heronclinic 3d mesh
        mesh.rotation.x = useAvaDisplayOrientation
          ? getMeshDisplayRotationX(fileName)
          : Math.PI / 2;
        mesh.rotation.y = useAvaDisplayOrientation
          ? getMeshDisplayRotationY(fileName)
          : Math.PI;
        mesh.rotation.z = useAvaDisplayOrientation
          ? getMeshDisplayRotationZ(fileName)
          : 0;
        // mesh.rotation.set(new THREE.Vector3( Math.PI / 2, Math.PI, 0));

        pivot.add(mesh);
        setArrObj((state) => [...state, mesh]);
      } else if (fileName.search('.ply') > 0 || fileName.search('.vmd') > 0) {
        const geometry = loadPly(fileBuffer);
        applyMeshTransform(geometry, meshTransforms, fileName);
        const material = createMeshMaterial(
          useAvaRendering,
          Boolean(geometry.getAttribute('color')),
        );

        material.polygonOffset = true;
        material.polygonOffsetFactor = 5;

        ambi_light.color.setHex(0xffffff);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData.fileName = fileName;
        mesh.userData.displayObject = mesh;

        //Rotate heronclinic 3d mesh
        mesh.rotation.x = useAvaDisplayOrientation
          ? getMeshDisplayRotationX(fileName)
          : Math.PI / 2;
        mesh.rotation.y = useAvaDisplayOrientation
          ? getMeshDisplayRotationY(fileName)
          : Math.PI;
        mesh.rotation.z = useAvaDisplayOrientation
          ? getMeshDisplayRotationZ(fileName)
          : 0;

        pivot.add(mesh);
        setArrObj((state) => [...state, mesh]);
      } else if (fileName.search('.obj') > 0) {
        var oloader = new OBJLoader();
        const mesh = oloader.parse(fileBuffer);
        const geometry = mesh.children[0].geometry;
        applyMeshTransform(geometry, meshTransforms, fileName);
        const material = createMeshMaterial(
          useAvaRendering,
          Boolean(geometry.getAttribute('color')),
        );
        material.polygonOffset = true;
        material.polygonOffsetFactor = 5;

        ambi_light.color.setHex(0xffffff);

        setArrObj((state) => [...state, mesh.children[0]]);
        mesh.children[0].material = material;
        mesh.children[0].userData.fileName = fileName;
        mesh.children[0].userData.displayObject = mesh;

        //Rotate heronclinic 3d mesh
        mesh.rotation.x = useAvaDisplayOrientation
          ? getMeshDisplayRotationX(fileName)
          : Math.PI / 2;
        mesh.rotation.y = useAvaDisplayOrientation
          ? getMeshDisplayRotationY(fileName)
          : Math.PI;
        mesh.rotation.z = useAvaDisplayOrientation
          ? getMeshDisplayRotationZ(fileName)
          : 0;

        pivot.add(mesh);
      } else if (fileName.search('margin') > 0) {
        const points = loadMarginPoints(fileBuffer);
        const geometry = new LineGeometry();
        geometry.setPositions(points);
        var resolution = new THREE.Vector2(screen.clientWidth, screen.clientHeight);
        const material = new LineMaterial({
          color: 0x00ff00,
          linewidth: 2, // in world units with size attenuation, pixels otherwise
          resolution: resolution,
          dashed: false,
          worldUnits: false,
          // alphaToCoverage: true,
        });

        material.polygonOffset = true;
        material.polygonOffsetFactor = -5;

        const mesh = new Line2(geometry, material);
        // mesh.computeLineDistances();
        // mesh.scale.set(1, 1, 1);
        setMarginObj((state) => [...state, mesh]);

        //Rotate heronclinic 3d mesh
        mesh.rotation.x = Math.PI / 2;
        mesh.rotation.y = Math.PI;

        pivot.add(mesh);
      }

      transControl.attach(pivot);
    });
  }, [dispatch, meshTransforms, pivot, resolvedFiles3D]);

  // Update materials without reloading or duplicating the meshes.
  useEffect(() => {
    arrObj.forEach((mesh) => {
      const vertexColors =
        !isSolidClicked && Boolean(mesh.geometry.getAttribute('color'));
      const materialType = useAvaRendering
        ? vertexColors
          ? 'ShaderMaterial'
          : 'MeshStandardMaterial'
        : 'MeshPhongMaterial';
      const previous = mesh.material;

      if (previous.type !== materialType || previous.vertexColors !== vertexColors) {
        const material = createMeshMaterial(useAvaRendering, vertexColors);
        material.opacity = previous.opacity;
        material.transparent = previous.transparent;
        material.depthWrite = previous.depthWrite;
        material.polygonOffset = previous.polygonOffset;
        material.polygonOffsetFactor = previous.polygonOffsetFactor;
        material.polygonOffsetUnits = previous.polygonOffsetUnits;
        mesh.material = material;
        previous.dispose();
      }

      if (mesh.material.uniforms?.uOpacity) {
        mesh.material.uniforms.uOpacity.value = mesh.material.opacity;
        mesh.material.uniforms.uSpecularEnabled.value = isSpecularLightEnabled ? 1 : 0;
      }
    });
  }, [arrObj, isSolidClicked, isSpecularLightEnabled, useAvaRendering]);

  //Move an axis the center of an object
  useEffect(() => {
    var sumx = 0,
      sumy = 0,
      sumz = 0;
    var vertexscount = 0;
    // var abutment_cnt = 0;

    if (fileCount === arrObj.length + marginObj.length) {
      console.debug('3D file loading completed >>> Center transformation started.');
      const loadingOverlay = document.querySelector('#loading-overlay');
      const progressBar = document.querySelector('#progress');
      loadingOverlay.style.display = 'none';
      progressBar.style.width = 0;

      setTimeout(() => {
        onMaximize();
      }, 100);

      for (let objectIndex = 0; objectIndex < arrObj.length; objectIndex++) {
        if (arrObj[objectIndex].geometry.type === 'BufferGeometry') {
          for (
            let vertexIndex = 0;
            vertexIndex < arrObj[objectIndex].geometry.attributes.position.count;
            vertexIndex += 1000
          ) {
            sumx +=
              arrObj[objectIndex].geometry.attributes.position.array[vertexIndex * 3];
            sumy +=
              arrObj[objectIndex].geometry.attributes.position.array[
                vertexIndex * 3 + 1
              ];
            sumz +=
              arrObj[objectIndex].geometry.attributes.position.array[
                vertexIndex * 3 + 2
              ];
            vertexscount++;
          }
        }
      }

      sumx = sumx / vertexscount;
      sumy = sumy / vertexscount;
      sumz = sumz / vertexscount;
      const center = new THREE.Vector3(sumx, sumy, sumz);
      const displayOffsets = {};
      const avaLayoutEntries = [];
      setMeshCenter(center);

      for (let marginIndex = 0; marginIndex < marginObj.length; marginIndex++) {
        marginObj[marginIndex].geometry.translate(-sumx, -sumy, -sumz);
      }

      for (let objectIndex = 0; objectIndex < arrObj.length; objectIndex++) {
        const object = arrObj[objectIndex];
        const objectFileName = object.userData.fileName || loading_order[objectIndex];
        const ownerMeshType = getOwnerMeshType(objectFileName);

        if (ownerMeshType !== null) {
          const displayOffset = new THREE.Vector3();
          const displayObject = object.userData.displayObject || object;
          displayObject.position.copy(displayOffset);
          displayOffsets[ownerMeshType] = displayOffset.toArray();

          if (useAvaDisplayOrientation) {
            object.geometry.computeBoundingBox();
            avaLayoutEntries.push({
              bounds: getRotatedBounds(
                object.geometry.boundingBox,
                center,
                new THREE.Euler(
                  getMeshDisplayRotationX(objectFileName),
                  getMeshDisplayRotationY(objectFileName),
                  getMeshDisplayRotationZ(objectFileName),
                ),
              ),
              displayObject,
              ownerMeshType,
            });
          }
        }

        arrObj[objectIndex].geometry.translate(-sumx, -sumy, -sumz);

        //Solid & Color
        if (fileType === 'ply' || fileType === 'vmd' || fileType === 'obj') {
          geoLen.push(arrObj[objectIndex].geometry.attributes.color.count * 3);
          colors.push(arrObj[objectIndex].geometry.attributes.color.array);

          for (let colorIndex = 0; colorIndex < geoLen[objectIndex]; colorIndex++) {
            temp_colors[objectIndex][colorIndex] =
              arrObj[objectIndex].geometry.attributes.color.array[colorIndex];
          }
        }
      }

      if (useAvaDisplayOrientation && avaLayoutEntries.length > 0) {
        const upperEntries = avaLayoutEntries.filter((entry) =>
          AVA_UPPER_MESH_TYPES.has(entry.ownerMeshType),
        );
        const lowerEntries = avaLayoutEntries.filter((entry) =>
          AVA_LOWER_MESH_TYPES.has(entry.ownerMeshType),
        );
        const upperBounds = getCombinedBounds(upperEntries);
        const lowerBounds = getCombinedBounds(lowerEntries);
        let upperOffset = upperBounds.isEmpty()
          ? null
          : getCenteredJawOffset(upperBounds);
        let lowerOffset = lowerBounds.isEmpty()
          ? null
          : getCenteredJawOffset(lowerBounds);

        if (upperOffset && lowerOffset) {
          const upperHeight = upperBounds.getSize(new THREE.Vector3()).y;
          const lowerHeight = lowerBounds.getSize(new THREE.Vector3()).y;
          const gap = Math.max(4, Math.min(12, (upperHeight + lowerHeight) * 0.08));
          upperOffset = getCenteredJawOffset(upperBounds, gap / 2 + upperHeight / 2);
          lowerOffset = getCenteredJawOffset(lowerBounds, -gap / 2 - lowerHeight / 2);
        }

        const layoutBounds = new THREE.Box3().makeEmpty();
        avaLayoutEntries.forEach((entry) => {
          const offset = AVA_UPPER_MESH_TYPES.has(entry.ownerMeshType)
            ? upperOffset
            : lowerOffset;
          if (!offset) return;

          entry.displayObject.position.copy(offset);
          displayOffsets[entry.ownerMeshType] = offset.toArray();
          layoutBounds.union(entry.bounds.clone().translate(offset));
        });

        if (!layoutBounds.isEmpty()) {
          const layoutSize = layoutBounds.getSize(new THREE.Vector3());
          const padding = 1.2;

          if (camera.isPerspectiveCamera) {
            const verticalFov = THREE.MathUtils.degToRad(camera.fov);
            const distanceForHeight =
              (layoutSize.y * padding) / (2 * Math.tan(verticalFov / 2));
            const distanceForWidth =
              (layoutSize.x * padding) /
              (2 * Math.tan(verticalFov / 2) * camera.aspect);
            camera.position.set(
              0,
              0,
              Math.max(cameraZ, distanceForHeight, distanceForWidth),
            );
          } else if (camera.isOrthographicCamera) {
            const visibleWidth = camera.right - camera.left;
            const visibleHeight = camera.top - camera.bottom;
            camera.zoom = Math.min(
              1,
              visibleWidth / (layoutSize.x * padding),
              visibleHeight / (layoutSize.y * padding),
            );
          }
          camera.updateProjectionMatrix();
        }
      }
      setFindingDisplayOffsets(displayOffsets);
    }
  }, [arrObj, marginObj, useAvaDisplayOrientation]);

  useEffect(() => {
    clearFindingMarkers(findingGroup);

    if (!meshCenter || findings.length === 0) {
      setSelectedFinding(null);
      return undefined;
    }

    createFindingMarkers(
      findingGroup,
      findings,
      meshCenter,
      meshTransforms,
      findingDisplayOffsets,
    );
    updateFindingMarkers(findingGroup, camera, renderer, findingHitTargets.current);

    setSelectedFinding(
      (current) =>
        findings.find(
          (finding) => current && getFindingKey(finding) === getFindingKey(current),
        ) || findings[0],
    );

    return () => clearFindingMarkers(findingGroup);
  }, [
    camera,
    findingDisplayOffsets,
    findingGroup,
    findings,
    meshCenter,
    meshTransforms,
    renderer,
  ]);

  // Grey Cube
  // const geometry = new THREE.BoxGeometry(5,5,5);
  // const material = new THREE.MeshBasicMaterial({color : 0xaaaaaa});
  // const cube = new THREE.Mesh(geometry, material);
  // cube.position.set(-25, -25- 0);
  // scene.add(cube);

  return (
    <div style={{ position: 'relative' }}>
      <div ref={ref} />

      <FindingsOverlay
        findings={findings}
        selectedFinding={selectedFinding}
        hitTargets={findingHitTargets}
        onSelect={setSelectedFinding}
      />

      {/* Preview en mode fullscreen */}
      {fullScreenState && (
        <CaseDetail3DPreview
          width={MINI_SCENE_WIDTH}
          height={MINI_SCENE_HEIGHT}
          mainScene={scene}
          mainCamera={camera}
          fullScreenState={fullScreenState}
        />
      )}
      {fileCount === arrObj.length + marginObj.length && (
        <SliderBox
          arrObj={arrObj}
          marginObj={marginObj}
          loading_order={loading_order}
          loading_order_margin={loading_order_margin}
          transControl={transControl}
          fileType={fileType}
          scene={scene}
          ambi_light={ambi_light}
          light={light}
          geoLen={geoLen}
          colors={colors}
          temp_colors={temp_colors}
          pivot={pivot}
          trackball={trackball}
          findingGroup={findingGroup}
          focusVisibleJawOnReset={useAvaDisplayOrientation}
          useAvaRendering={useAvaRendering}
          isSolidClicked={isSolidClicked}
          onSolidChange={setIsSolidClicked}
          isSpecularLightEnabled={isSpecularLightEnabled}
          onSpecularLightChange={setIsSpecularLightEnabled}
        />
      )}
      {!fullScreenState && fileCount === arrObj.length + marginObj.length && (
        <div
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 13,
            userSelect: 'none',
          }}
        >
          {/* <img
            style={{
              position: 'absolute',
              marginTop: '16px',
              marginRight: '16px',
              right: '0',
              userSelect: 'none',
            }}
            src={maximizeActive}
            onMouseOver={(e) => (e.currentTarget.src = maximizeHover)}
            onMouseOut={(e) => (e.currentTarget.src = maximizeActive)}
            onMouseDown={(e) => (e.currentTarget.src = maximizeSelected)}
            onClick={onMaximize}
            alt="maximizeIcon"
          /> */}
          <div onClick={onMaximize}>
            <SVG
              name="maximize"
              color="#878D93"
              colorOver={themeContext.main_color_hover}
              colorOut="#878D93"
              colorDown={themeContext.main_color}
            />
          </div>
        </div>
      )}
      {fullScreenState && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 13,
            userSelect: 'none',
          }}
        >
          {/* <img
            style={{
              position: 'absolute',
              marginTop: '20px',
              marginRight: '20px',
              right: '0',
              userSelect: 'none',
            }}
            src={closeActive}
            onMouseOver={(e) => (e.currentTarget.src = closeHover)}
            onMouseOut={(e) => (e.currentTarget.src = closeActive)}
            onMouseDown={(e) => (e.currentTarget.src = closeSelected)}
            onClick={onClose}
            alt="closeIcon"
          /> */}
          <div onClick={onClose}>
            <SVG
              name="close"
              color="#878D93"
              colorOver={themeContext.main_color_hover}
              colorOut="#878D93"
              colorDown={themeContext.main_color}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(BasicScene);
